import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, split } from '@apollo/client/core'
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { createApolloProvider } from '@vue/apollo-option'
import ActionCable from 'actioncable'
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink'
import { Notify } from 'quasar'
import { actionCableUri, appEnv, appMode, circuitId, gqlUri, hasActionCable, isConsumer, isElectronMode, siteId } from 'src/config'
import Model from 'src/models/Model'

export default async ({ app, store }) => {
  if (appEnv !== 'production') {
    // As of Apollo Client 3.8, error messages are excluded from the core package to reduce bundle size. Instead, a console
    // error is shown with a URL pointing to an external page displaying the error messages. These functions opt back into
    // showing the error messages in the console. This loads more code, so we may only want to do it on dev env.
    // More info: https://www.apollographql.com/docs/react/errors
    loadDevMessages()
    loadErrorMessages()
  }

  // HTTP connection to the API
  const headers = {}

  headers['is-electron-mode'] = isElectronMode

  if (isConsumer) {
    headers['site-id'] = siteId
    // Only 1 of these 2 values should be populated, if circuitId is populated it is for a single checkout flow supporting multiple sites of a circuit
    headers['circuit-id'] = circuitId
  }
  headers['client-type'] = appMode

  const httpLink = new HttpLink({
    uri: gqlUri,
    credentials: 'include',
    headers
  })

  let combinedLink
  let cable
  if (hasActionCable) {
    cable = ActionCable.createConsumer(`${actionCableUri}?clientType=${appMode}`)
    app.config.globalProperties.$cable = cable

    const hasSubscriptionOperation = ({ query: { definitions } }) => {
      return definitions.some(
        ({ kind, operation }) => kind === 'OperationDefinition' && operation === 'subscription'
      )
    }

    combinedLink = split(
      hasSubscriptionOperation,
      new ActionCableLink({ cable, headers }),
      httpLink
    )
  } else {
    combinedLink = httpLink
  }

  // Cache implementation
  const cache = new InMemoryCache()

  // Global error handler
  const errorHandlerLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors && graphQLErrors.length) {
      const message = (graphQLErrors && graphQLErrors.length && graphQLErrors[0].message) || ''
      console.log(message)

      // eslint-disable-next-line camelcase
      graphQLErrors.map(({ message, message_without_codes, locations, path, extensions }) => {
        // eslint-disable-next-line camelcase
        logRollbarError(`[GraphQL error]: Message: ${message_without_codes || message}, Location: ${JSON.stringify(locations)}, Path: ${path},  Extensions: ${JSON.stringify(extensions)}`, { message })
      })

      if (message) {
        Notify.create({
          message,
          type: 'negative',
          position: 'top'
        })
      }
    }

    const networkErrorMessage = networkError?.result?.error?.message
    const recoverable = networkError?.result?.error?.recoverable

    if (networkErrorMessage && !recoverable) {
      Notify.create({
        message: networkErrorMessage,
        type: 'negative',
        position: 'top'
      })
    } else if (networkError?.message === 'Failed to fetch') {
      if (window.dataLoadRetry) {
        // Do nothing, handled elsewhere
      } else if (window.navigator.onLine) {
        Notify.create({
          message: 'We are having trouble reaching the server, please try again',
          position: 'top'
        })
      } else {
        Notify.create({
          message: 'You appear to be offline. Please connect to the internet and try again',
          position: 'top'
        })
      }
    } else if (!recoverable && (!graphQLErrors || !graphQLErrors.length)) {
      Notify.create({
        message: 'An unhandled error occurred: ' + networkError,
        type: 'negative',
        position: 'top'
      })
    }

    if (networkError) {
      const operationName = _.get(operation, 'query.definitions[0].selectionSet.selections[0].name.value')
      const errorDetails = {
        operationName,
        variables: operation.variables,
        name: networkError.name,
        message: networkErrorMessage || networkError.message,
        bodyText: networkError.bodyText,
        statusCode: networkError.statusCode,
        stack: networkError.stack
      }

      if (networkErrorMessage) {
        logRollbarError(`[Network error message]: ${networkErrorMessage}`, errorDetails)
      } else {
        logRollbarError(`[Network error]: ${networkError}`, errorDetails)
      }
    }
  })

  let needSiteValidation = false

  const siteChangeLink = new ApolloLink((operation, forward) => {
    if (!needSiteValidation) {
      return forward(operation)
    }

    const previousSiteId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('site_id='))
      ?.split('=')[1]

    return forward(operation).map((data) => {
      const currentSiteId = document.cookie
        .split('; ')
        .find((row) => row.startsWith('site_id='))
        ?.split('=')[1]

      if (!siteId && previousSiteId !== currentSiteId) {
        app.$handleSiteChange()
      }

      return data
    })
  })

  // Retries on failed to fetch (default options there for visibility) https://www.apollographql.com/docs/react/api/link/apollo-link-retry/
  const WAIT_FOR_RETRY_MS = 300
  const retryIf = (error, operation) => {
    if (!error) {
      return false
    }

    if (error.message !== 'Failed to fetch') {
      return false
    }

    const operationType = _.get(operation, 'query.definitions[0].operation')

    // We don't retry mutations or subscriptions
    if (operationType !== 'query') {
      return false
    }

    const operationName = _.get(operation, 'query.definitions[0].selectionSet.selections[0].name.value')

    // For iOS, we allow retrying clientContext queries as they seem to fail more often
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
    if (isIOS && operationName === 'clientContext') {
      return true
    }

    // We don't retry clientContext but we don't know why. Leaving for now in case it was a good reason
    return operationName !== 'clientContext'
  }

  const retryLink = new RetryLink({
    delay: {
      initial: WAIT_FOR_RETRY_MS,
      max: Infinity,
      jitter: true
    },
    attempts: {
      max: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 10 : 5,
      retryIf
    }
  })

  // Only checking for site change link for websites handling multiple sites for a circuit e.g. Screen Machine
  const apolloLinks = siteId ? [errorHandlerLink, retryLink, combinedLink] : [errorHandlerLink, retryLink, siteChangeLink, combinedLink]

  // Create the apollo client
  const apolloClient = new ApolloClient({
    link: ApolloLink.from(apolloLinks),
    cache
    // ,
    // defaultOptions: {
    //   watchQuery: {
    //     fetchPolicy: 'cache-and-network',
    //     errorPolicy: 'all'
    //   },
    //   query: {
    //     fetchPolicy: 'cache-and-network',
    //     errorPolicy: 'all'
    //   },
    //   mutate: {
    //     errorPolicy: 'all'
    //   }
    // }
  })

  // Expose the client to the store to provide actions access
  store.apolloClient = apolloClient
  Model.apollo = apolloClient
  Model.store = store
  Model.getters = store.getters
  Model.app = app
  Model.cable = cable

  const apolloProvider = createApolloProvider({
    defaultClient: apolloClient
  })

  app.$updateGlobalHeaders = function (newHeaders, resetCache = false) {
    _.each(newHeaders, (value, key) => {
      if (key === 'showing-id' && headers[key] !== newHeaders[key]) {
        needSiteValidation = true
      }

      if (value === null) {
        delete headers[key]
      } else {
        headers[key] = value
      }
    })
    if (resetCache) {
      cache.reset()
    }
  }

  app.$clearGlobalHeaders = function (...keys) {
    _.each(keys, (key) => {
      delete headers[key]
    })
  }

  function logRollbarError (message, extraArgs = {}) {
    console.log(message)

    if (typeof Rollbar === 'undefined') return

    // eslint-disable-next-line no-undef
    Rollbar.critical(message, extraArgs)
  }

  // attach created 'apollo provider' instance to the app
  app.apolloProvider = apolloProvider

  app.use(apolloProvider)
}
